import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Drawer } from 'vaul';
import AppHeader from '../components/AppHeader';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import { getLoad, updateLoadStatus } from '../api/loadsApi';
import { getFullPhotoUrl } from '../utils/imageUrl';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';

function LoadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [updateError, setUpdateError] = useState('');

  useEffect(() => {
    async function fetchLoad() {
      try {
        const result = await getLoad(id);
        if (result.success) {
          setLoad(result.load);
        } else {
          setError(result.message || 'Failed to load data.');
        }
      } catch (err) {
        setError('Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }

    fetchLoad();
  }, [id]);

  const nextStatusOptions = {
    awaiting: [{ value: 'picked up', label: 'Mark as Picked Up' }],
    'picked up': [{ value: 'delivered', label: 'Mark as Delivered' }],
    delivered: [],
    canceled: [],
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdateError('');
    try {
      const result = await updateLoadStatus(load.id_load, newStatus);
      if (result.success) {
        setLoad((prev) => ({ ...prev, load_status: newStatus }));
        setSheetOpen(false);
        showToast('success', 'Status updated successfully.');
      } else {
        setUpdateError(result.message || 'Failed to update status.');
        showToast('error', result.message || 'Failed to update status.');
      }
    } catch (err) {
      setUpdateError('Could not connect to server.');
      showToast('error', t('common.networkError'));
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Load" showBack={true} fallbackRoute="/loads" />
        <LoadingSpinner fullScreen />
      </div>
    );
  }

  if (error || !load) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Error" showBack={true} fallbackRoute="/loads" />
        <div style={{ padding: 16, color: '#dc2626' }}>{error || 'Load not found.'}</div>
      </div>
    );
  }

  const availableActions = nextStatusOptions[load.load_status] || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppHeader title={`Load #${load.id_load}`} showBack={true} fallbackRoute="/loads" />

      <div style={{ padding: 16 }}>
        <div style={styles.card}>
          <div style={styles.routeHeader}>
            <span style={styles.routeText}>{load.origin}</span>
            <span style={styles.arrow}>→</span>
            <span style={styles.routeText}>{load.destination}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.metaText}>{load.distance}</span>
            <StatusBadge status={load.load_status} />
          </div>
          {/* Shows the DRIVER's own price (their computed cut) —
              never the real load price, which only dispatchers/admins see. */}
          {load.driver_price != null && (
            <div style={styles.priceText}>
              {Number(load.driver_price).toLocaleString()} {load.price_currency}
            </div>
          )}
          <div style={styles.datesRow}>
            <div>
              <div style={styles.dateLabel}>Pick up</div>
              <div style={styles.dateValue}>{load.pick_up_date}</div>
            </div>
            <div>
              <div style={styles.dateLabel}>Delivery</div>
              <div style={styles.dateValue}>{load.delivery_date}</div>
            </div>
          </div>
        </div>

        <div style={styles.sectionTitle}>Vehicle(s)</div>
        {load.vehicles.map((vehicle) => {
          const firstPhotoRelative = vehicle.car_photos_url?.[0];
          const firstPhotoFull = getFullPhotoUrl(firstPhotoRelative);

          return (
            <div
              key={vehicle.id_vehicle}
              style={styles.vehicleCard}
              onClick={() => navigate(`/loads/${load.id_load}/vehicles/${vehicle.id_vehicle}`)}
            >
              {firstPhotoFull ? (
                <img src={firstPhotoFull} alt={vehicle.vehicle_name} style={styles.vehicleThumb} />
              ) : (
                <div style={styles.vehicleThumbPlaceholder}>🚗</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={styles.vehicleName}>
                  {vehicle.vehicle_name} {vehicle.vehicle_model}
                </div>
                <div style={styles.vin}>VIN: {vehicle.vin_number}</div>
              </div>
              <StatusBadge status={vehicle.vehicle_status} />
            </div>
          );
        })}
      </div>

      {availableActions.length > 0 && (
        <div style={styles.actionBar}>
          <button style={styles.actionButton} onClick={() => setSheetOpen(true)}>
            Update Status
          </button>
        </div>
      )}

      <Drawer.Root open={sheetOpen} onOpenChange={setSheetOpen}>
        <Drawer.Portal>
          <Drawer.Overlay style={styles.overlay} />
          <Drawer.Content style={styles.sheetContent}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitle}>Update Load Status</div>
            {updateError && <p style={styles.sheetError}>{updateError}</p>}
            {availableActions.map((action) => (
              <button
                key={action.value}
                style={styles.sheetOption}
                onClick={() => handleStatusUpdate(action.value)}
              >
                {action.label}
              </button>
            ))}
            <button style={styles.sheetCancel} onClick={() => setSheetOpen(false)}>
              Cancel
            </button>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  routeHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
  },
  routeText: {
    fontWeight: 700,
    fontSize: 16,
    color: '#111827',
  },
  arrow: {
    margin: '0 8px',
    color: '#9ca3af',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  metaText: {
    fontSize: 13,
    color: '#6b7280',
  },
  priceText: {
    fontSize: 16,
    fontWeight: 700,
    color: '#2563eb',
    marginBottom: 14,
  },
  datesRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid #f3f4f6',
    paddingTop: 12,
  },
  dateLabel: {
    fontSize: 11,
    color: '#9ca3af',
    marginBottom: 2,
  },
  dateValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#374151',
    marginBottom: 10,
  },
  vehicleCard: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  vehicleThumb: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: 'cover',
    marginRight: 12,
    flexShrink: 0,
  },
  vehicleThumbPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    fontSize: 24,
    flexShrink: 0,
  },
  vehicleName: {
    fontWeight: 700,
    fontSize: 14,
    color: '#111827',
    marginBottom: 2,
  },
  vin: {
    fontSize: 12,
    color: '#6b7280',
  },
  actionBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    padding: 16,
    boxShadow: '0 -1px 6px rgba(0,0,0,0.08)',
  },
  actionButton: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 60,
  },
  sheetContent: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: '10px 20px 24px',
    zIndex: 70,
  },
  sheetHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#d1d5db',
    borderRadius: 999,
    margin: '0 auto 16px',
  },
  sheetTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 16,
    textAlign: 'center',
  },
  sheetError: {
    color: '#dc2626',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 10,
  },
  sheetOption: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 600,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    marginBottom: 10,
    cursor: 'pointer',
  },
  sheetCancel: {
    width: '100%',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    fontWeight: 600,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
  },
};

export default LoadDetail;